import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle, ChevronDown, CreditCard, Download, FileDown, FileText, RefreshCw, Trash2 } from 'lucide-react';
import { formatUTC } from './utils';
import { formatMoney } from '@/lib/money';
import { ReportStatusBadge, InvoiceStatusBadge } from './StatusBadge';
import { ReportDetailsExpanded } from './ReportDetailsExpanded';

interface BillingReportsListProps {
  reports: any[];
  isLoading: boolean;
  isAccountManager: boolean;
  onRefresh: () => void;
  onRetry: (reportId: string, accountId: string) => void;
  onDelete: (reportId: string, accountId: string) => void;
  onUpdatePayment: () => void;
  onOpenInvoice: (reportId: string, target: 'hosted' | 'pdf') => void;
  isRetrying: boolean;
  isDeleting: boolean;
  isUpdatingPayment: boolean;
  openingInvoiceReportId: string | null;
}

export function BillingReportsList({
  reports,
  isLoading,
  isAccountManager,
  onRefresh,
  onRetry,
  onDelete,
  onUpdatePayment,
  onOpenInvoice,
  isRetrying,
  isDeleting,
  isUpdatingPayment,
  openingInvoiceReportId
}: BillingReportsListProps) {
  const { t } = useTranslation('billing');
  const [expandedReport, setExpandedReport] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <CardTitle>{t('reports.title')}</CardTitle>
            <CardDescription className="mt-1">{t('reports.description')}</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isLoading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('reports.columns.period')}</TableHead>
              <TableHead>{t('reports.columns.status')}</TableHead>
              <TableHead>{t('reports.columns.approved')}</TableHead>
              <TableHead>{t('reports.columns.subclub')}</TableHead>
              <TableHead>{t('reports.columns.declined')}</TableHead>
              <TableHead>{t('reports.columns.refunded')}</TableHead>
              <TableHead>{t('reports.columns.totalVolume')}</TableHead>
              <TableHead>{t('reports.columns.totalFees')}</TableHead>
              <TableHead>{t('reports.columns.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="py-8 text-center">
                  <RefreshCw className="mx-auto h-5 w-5 animate-spin" />
                </TableCell>
              </TableRow>
            ) : reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-muted-foreground text-center">
                  {t('reports.noReports')}
                </TableCell>
              </TableRow>
            ) : (
              reports.map((report: any) => (
                <>
                  <TableRow
                    key={report.id}
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => setExpandedReport(expandedReport === report.id ? null : report.id)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${expandedReport === report.id ? 'rotate-180' : ''}`}
                        />
                        <div>
                          <div className="font-medium">
                            {formatUTC(report.periodStart, 'MMM dd')} - {formatUTC(report.periodEnd, 'MMM dd, yyyy')}
                          </div>
                          <div className="text-muted-foreground text-xs">{t('reports.reportIdLabel')} {report.id}</div>
                          {report.status === 'failed' && report.errorMessage && (
                            <div className="text-destructive mt-1 flex items-center gap-1 text-xs">
                              <AlertTriangle className="h-3 w-3" />
                              <span>{report.errorMessage}</span>
                            </div>
                          )}
                          {(report.stripeInvoiceStatus === 'open' || report.stripeInvoiceStatus === 'past_due') && (
                            <div className="mt-1 flex items-center gap-1 text-xs text-amber-600">
                              <CreditCard className="h-3 w-3" />
                              <span>{t('reports.paymentPending')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {report.status !== 'completed' && <ReportStatusBadge status={report.status} />}
                        <InvoiceStatusBadge status={report.stripeInvoiceStatus} />
                      </div>
                    </TableCell>
                    <TableCell>{report.approvedTransactionCount?.toLocaleString() || 0}</TableCell>
                    <TableCell>{report.subclubTransactionCount?.toLocaleString() || 0}</TableCell>
                    <TableCell>{report.declinedTransactionCount?.toLocaleString() || 0}</TableCell>
                    <TableCell>{report.refundCount?.toLocaleString() || 0}</TableCell>
                    <TableCell>${report.totalTransactionVolume || 0}</TableCell>
                    <TableCell>{formatMoney(report.totalFeesAmount || 0, 'USD')}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2">
                        {report.status === 'failed' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onRetry(report.id, report.accountId)}
                              disabled={isRetrying}
                            >
                              <RefreshCw className="mr-1 h-4 w-4" />
                              {t('reports.actions.retry')}
                            </Button>
                            {isAccountManager && (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => onDelete(report.id, report.accountId)}
                                disabled={isDeleting}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        )}
                        {/* Gated on the invoice id, not the stored URL: the id is what
                            resolves a fresh link, and the stored URL has expired for
                            anything older than a month. #TAG-2261 */}
                        {report.stripeInvoiceId && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onOpenInvoice(report.id, 'hosted')}
                              disabled={openingInvoiceReportId === report.id}
                            >
                              <FileText className="mr-1 h-4 w-4" />
                              {t('reports.actions.invoice')}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onOpenInvoice(report.id, 'pdf')}
                              disabled={openingInvoiceReportId === report.id}
                            >
                              <FileDown className="mr-1 h-4 w-4" />
                              {t('reports.actions.pdf')}
                            </Button>
                          </>
                        )}
                        {(report.stripeInvoiceStatus === 'open' || report.stripeInvoiceStatus === 'past_due') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={onUpdatePayment}
                            disabled={isUpdatingPayment}
                          >
                            <CreditCard className="mr-1 h-4 w-4" />
                            {t('reports.actions.updatePayment')}
                          </Button>
                        )}
                        {report.rawDataUrl && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(report.rawDataUrl, '_blank')}
                          >
                            <Download className="mr-1 h-4 w-4" />
                            {t('reports.actions.csv')}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>

                  {expandedReport === report.id && (
                    <ReportDetailsExpanded reportId={report.id} report={report} />
                  )}
                </>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
